import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Button, IconButton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CloseIcon from '@mui/icons-material/Close';
import { useMediaQuery, useTheme } from "@mui/material";

// --- Configuration ---
const INITIAL_PADDING_UNIT = 2;
const SCROLL_THRESHOLD = 400;

// --- Image Data ---
const pergolaImages = [
  { id: 1, src: '/assets/images/landing/landing_1.jpg', alt: 'Modern pergola with seating area' },
  { id: 2, src: '/assets/images/landing/landing_2.jpg', alt: 'Elegant backyard pergola installation' },
  { id: 3, src: '/assets/images/landing/landing_3.jpg', alt: 'Custom wooden pergola over a patio' },
  { id: 4, src: '/assets/images/landing/landing_4.jpg', alt: 'Sleek metal pergola design' },
  { id: 5, src: '/assets/images/landing/landing_5.jpg', alt: 'Pergola with integrated garden features' },
  { id: 6, src: '/assets/images/landing/landing_6.jpg', alt: 'Pergola providing shade for outdoor dining' },
  { id: 7, src: '/assets/images/landing/landing_7.jpg', alt: 'A classic pergola in a landscaped garden' },
  { id: 8, src: '/assets/images/landing/landing_8.jpg', alt: 'Poolside pergola structure for relaxation' },
  { id: 9, src: '/assets/images/landing/landing_9.jpg', alt: 'Close-up detail of pergola construction' },
];

function LandingHero() {
  const [padding, setPadding] = useState(INITIAL_PADDING_UNIT);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showInfoBlock, setShowInfoBlock] = useState(false);
  const [showQuoteBanner, setShowQuoteBanner] = useState(false);

  const handleClick = () => {
  document.getElementById("video-feature")?.scrollIntoView({ behavior: "smooth" });
};

  // Effect for scroll events
  useEffect(() => {
    const handleScroll = () => {
      const newPadding = Math.max(0, INITIAL_PADDING_UNIT * (1 - window.scrollY / SCROLL_THRESHOLD));
      setPadding(newPadding);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Effect to trigger animations on load
  useEffect(() => {
    const loadTimer = setTimeout(() => setIsLoaded(true), 100);
    const showInfoTimer = setTimeout(() => setShowInfoBlock(true), 1500);
    const quoteTimer = setTimeout(() => setShowQuoteBanner(true), 1200); // banner slides in
    return () => {
      clearTimeout(loadTimer);
      clearTimeout(showInfoTimer);
      clearTimeout(quoteTimer);
    };
  }, []);

  const handleScrollDown = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  const handleQuoteClick = () => {
    document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" });
  };

    const theme = useTheme();
    const isExtraSmallSize =  useMediaQuery(theme.breakpoints.down("sm"));

  console.log(isExtraSmallSize);

  return (
    <>
      <Box sx={{ height: '100vh', boxSizing: 'border-box', backgroundColor: '#1a1a1a', position: 'relative', overflow: 'hidden' }}>
        {/* Mosaic */}
        <Grid container spacing={0} sx={{ height: '100%' }}>
          {pergolaImages.map((image, index) => (
            <Grid
              item
              key={image.id}
              size={{xs: 6, md: 4}} // if using MUI v5, switch to xs={4}
              sx={{
                height: {xs: '33.3%', md: '33.33%'},
                boxSizing: 'border-box',
                p: padding,
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.6s ease-out, transform 0.6s ease-out, padding 0.1s ease-out',
                transitionDelay: isLoaded ? `${index * 100}ms, ${index * 100}ms, 0ms` : '0ms',
              }}
            >
              <Box component="img" src={image.src} alt={image.alt} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </Grid>
          ))}
        </Grid>

        {/* Subtle scrim to improve logo legibility */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            pointerEvents: 'none',
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.45) 100%)',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.6s ease-out',
          }}
        />

        {/* FULL-WIDTH LOGO OVERLAY WITH WHITE TRANSLUCENT ROUNDED RECTANGLE */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none', // allows underlying CTA to remain clickable
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 4 },
              py: { xs: 1.5, sm: 2.5 },
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.75)',
              backdropFilter: 'blur(4px)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
              transform: isLoaded ? 'translateY(0)' : 'translateY(8px)',
              opacity: isLoaded ? 1 : 0,
              transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
            }}
          >
            <Box
              component="img"
              src="/assets/images/wrightglazing_logo_x_blinds.png" // <-- swap to your logo path
              alt="WrightGlazing"
              sx={{
                display: 'block',
                width: '100%',
                maxWidth: {md: '50vw', xs: '85vw'},            // <= requested cap
                height: 'auto',
                maxHeight: { xs: '18vh', sm: '22vh', md: '26vh' },
                filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.35))',
                userSelect: 'none',
              }}
            />
          </Box>
        </Box>

        {/* Scroll-to-content chevron */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 5,
          }}
        >
          <IconButton
            aria-label="Scroll to content"
            onClick={handleScrollDown}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.9)',
              borderRadius: '9999px',
              boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
              pointerEvents: 'auto', // clickable even though overlays are pointerEvents: none
              '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
              animation: 'floatY 1.8s ease-in-out infinite',
            }}
          >
            <KeyboardArrowDownIcon fontSize="large" />
          </IconButton>
        </Box>

        {/* <Box
          role="complementary"
          aria-label="Get a Quote"
          disableGutters={isExtraSmallSize ? true : false}
          sx={{
            position: 'fixed',
            display: { md: 'none', sm: 'none', xs: 'none'} ,
            zIndex: 10, // above chevron (5) and overlays (2/3)
            right: { xs: 12, md: 24 },
            left: { xs: 12, md: 'auto' },
            bottom: { xs: 12, md: 24 },
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: { xs: 1.5, md: 2 },
            py: { xs: 1, md: 1 },
            borderRadius: 9999,
            backgroundColor: 'rgba(22, 161, 216, 0.98)', // brand blue
            color: '#fff',
            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
            backdropFilter: 'blur(2px)',
            pointerEvents: 'auto',
            width: { xs: 'auto', md: 'auto' },
            maxWidth: { xs: '200px', md: 'unset' },
            transform: showQuoteBanner ? 'translateY(0)' : 'translateY(16px)',
            opacity: showQuoteBanner ? 1 : 0,
            transition: 'opacity .35s ease, transform .35s ease',
          }}
        >

          <Button
            onClick={handleQuoteClick}
            size="small"
            variant="contained"
            sx={{
              ml: 0.5,
              backgroundColor: '#1a1a1a',
              color: '#fff',
              borderRadius: 9999,
              px: 2,
              py: 0.75,
              '&:hover': { backgroundColor: '#141414' },
            }}
          >
            Get a Free Quote
          </Button>

          <IconButton
            aria-label="Close"
            onClick={() => setShowQuoteBanner(false)}
            sx={{
              ml: 0.5,
              color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.15)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box> */}
        {/* <style>
          {`
            @keyframes floatY {
              0%   { transform: translate(-50%, 0); }
              50%  { transform: translate(-50%, 6px); }
              100% { transform: translate(-50%, 0); }
            }
          `}
        </style> */}
      </Box>
    </>
  );
}

export default LandingHero;
